// src/components/SpatialOffers.jsx
import { useEffect, useState, useRef } from "react";
import { Card, Spin, Button, Typography, Carousel } from "antd";
import axiosInstance from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

// ✅ slick styles (important)
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const { Title } = Typography;

const SpatialOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const carouselRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        // const res = await axiosInstance.get(
        //   `/product-details?filters[product_groups][name][$eq]=is_spatial_offer&populate[images]=true&populate[sub_product][populate][products][populate]=images`,
        // );

        const res = await axiosInstance.get(
          `/product-details?filters[product_groups][name][$eq]=is_spatial_offer&fields[0]=title&fields[1]=slug&fields[2]=final_price&fields[3]=old_price&fields[4]=save_price&populate[images][fields][0]=url&populate[sub_product][fields][0]=slug`,
        );
        const data = res.data.data || [];

        const formatted = data.map((offer, index) => {
          const title =
            offer?.title || offer?.attributes?.title || "Untitled Offer";

          const imageUrl =
            offer?.images?.[0]?.url ||
            offer?.attributes?.images?.data?.[0]?.attributes?.url ||
            "/placeholder.png";

          return {
            id: offer?.id || index,
            title,
            img: `${process.env.REACT_APP_API_URL}${imageUrl}`,
            desc:
              offer?.desc || offer?.attributes?.desc || "On Domestic Hotels",
            note:
              offer?.note ||
              offer?.attributes?.note ||
              "*Offers is Valid Only On Confirmed Hotel Bookings",
            raw: offer,
          };
        });

        setOffers(formatted);
      } catch (error) {
        console.error("Error fetching Spatial Offers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const onCardClick = (offer) => {
    const data = offer.raw;

    const productSlug =
      data.sub_product?.product?.slug ||
      data?.attributes?.sub_product?.data?.attributes?.product?.data?.attributes
        ?.slug;

    const subProductSlug =
      data.sub_product?.slug ||
      data?.attributes?.sub_product?.data?.attributes?.slug;

    const detailSlug = data.slug || data?.attributes?.slug;

    if (productSlug && subProductSlug && detailSlug) {
      navigate(`/${subProductSlug}/${detailSlug}`);
    }
  };

  // ✅ Loading
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin size="large" tip="Loading Spatial Offers..." />
      </div>
    );
  }

  // ✅ Empty
  if (!offers.length) {
    return (
      <div className="container py-10 text-center text-gray-500">
        No Spatial Offers available right now.
      </div>
    );
  }

  return (
    <div className="">
      {/* Header + Arrows */}

      <div className="relative">
        {/* LEFT */}
        <button
          onClick={() => carouselRef.current.prev()}
          className="absolute left-0 lg:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow rounded-full flex items-center justify-center"
        >
          <LeftOutlined />
        </button>

        {/* RIGHT */}
        <button
          onClick={() => carouselRef.current.next()}
          className="absolute -right-0 lg:-right-5  top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow rounded-full flex items-center justify-center"
        >
          <RightOutlined />
        </button>

        <Carousel
          ref={carouselRef}
          className="spatial-carousel"
          dots={false}
          infinite={false}
          slidesToShow={3}
          slidesToScroll={1}
          responsive={[
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1 } },
          ]}
        >
          {offers.map((offer, index) => (
            <div key={offer.id} className="px-2 h-full">
              {/* ✅ FULL HEIGHT WRAPPER */}
              <div className="h-full flex">
                <Card
                  onClick={() => onCardClick(offer)}
                  className="flex flex-col w-full h-full rounded-[20px] overflow-hidden border-[#E5E5E5] cursor-pointer hover:shadow-md transition-all"
                  styles={{ body: { padding: 0, height: "100%" } }}
                >
                  <div className="flex h-full max-[250px]:">
                    {/* LEFT IMAGE */}
                    <div className="relative w-[45%] overflow-hidden rounded-r-full">
                      <div className="absolute bg-black/10 w-[95%] h-full rounded-r-full"></div>
                      <img
                        src={offer.img}
                        alt={offer.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* RIGHT CONTENT */}
                    <div className="w-[55%] p-5 pl-4 pr-2 flex flex-col h-full">
                      {/* TOP CONTENT */}
                      <div>
                        <h3 className="text-base mb-2 font-medium line-clamp-2">
                          {offer.title}
                        </h3>

                        <p className="text-[10px] mb-3 text-gray-600">
                          {offer.desc}
                        </p>

                        <small className="text-[10px] text-gray-500">
                          {offer.note}
                        </small>
                      </div>

                      {/* ✅ PUSH BUTTON TO BOTTOM */}
                      <div className="mt-auto">
                        <Button
                          type="primary"
                          className="mt-3 text-[11px] bg-red-600 hover:bg-red-700 border-none text-white w-fit"
                          onClick={(e) => {
                            e.stopPropagation();

                            const data = offer.raw;

                            const subProductSlug =
                              data.sub_product?.slug ||
                              data?.attributes?.sub_product?.data?.attributes
                                ?.slug;

                            const detailSlug =
                              data.slug || data?.attributes?.slug;

                            if (subProductSlug && detailSlug) {
                              navigate(`/${subProductSlug}/${detailSlug}`);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default SpatialOffers;
